<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useChatStore } from '@/stores/chat'
import { storeToRefs } from 'pinia'
import { useScroll } from '@vueuse/core'
import hljs from 'highlight.js'
import 'highlight.js/styles/github-dark.css'

const chatStore = useChatStore()
const { currentMessages, uiState } = storeToRefs(chatStore)
const inputMessage = ref('')
const messagesContainer = ref<HTMLElement>()
const inputRef = ref<HTMLTextAreaElement>()
const showCopyTip = ref(false)

const { y } = useScroll(messagesContainer)

// 检测是否包含代码块
function detectAndFormatCode(text: string) {
  // 检测是否是代码块
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g
  return text.replace(codeBlockRegex, (match, lang, code) => {
    try {
      const language = lang || 'plaintext'
      const highlighted = hljs.highlight(code.trim(), { language }).value
      return `<pre class="code-block"><div class="code-header"><span class="code-lang">${language}</span></div><code class="hljs ${language}">${highlighted}</code></pre>`
    } catch (e) {
      return match
    }
  })
}

// 复制功能增强
async function copyToClipboard(text: string, event?: Event) {
  try {
    await navigator.clipboard.writeText(text)
    showCopyTip.value = true
    setTimeout(() => {
      showCopyTip.value = false
    }, 2000)
  } catch (err) {
    console.error('复制失败:', err)
  }
}

async function handleSubmit() {
  if (!inputMessage.value.trim() || uiState.value.isLoading) return
  
  const message = inputMessage.value
  inputMessage.value = '' // 立即清空输入
  
  // 自动调整输入框高度
  if (inputRef.value) {
    inputRef.value.style.height = 'auto'
  }
  
  await chatStore.sendUserMessage(message)
}

function adjustTextareaHeight(e: Event) {
  const textarea = e.target as HTMLTextAreaElement
  textarea.style.height = 'auto'
  textarea.style.height = textarea.scrollHeight + 'px'
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSubmit()
  }
}

function scrollToBottom() {
  if (messagesContainer.value)
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
}

onMounted(scrollToBottom)
watch(currentMessages, scrollToBottom, { deep: true })
</script>

<template>
  <div class="flex flex-col h-[100dvh] bg-gradient-to-b from-gray-50 to-white">
    <!-- 消息列表 -->
    <div 
      ref="messagesContainer"
      class="flex-1 overflow-y-auto px-3 sm:px-4 pt-safe pb-2 space-y-3 messagesContainer bg-gradient-to-b from-blue-50/30 to-gray-50/30"
    >
      <!-- 添加一个顶部占位元素 -->
      <div class="h-12"></div>
      
      <div class="max-w-3xl mx-auto w-full space-y-3">
        <div
          v-for="message in currentMessages"
          :key="message.id"
          class="flex animate-fade-in"
          :class="[
            message.role === 'user' ? 'justify-end' : 'justify-start'
          ]"
        >
          <!-- AI头像 -->
          <div v-if="message.role === 'assistant'" class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mr-2 flex-shrink-0 shadow-sm">
            <img src="@/assets/0.jpg" alt="" class="rounded-full w-full">
          </div>

          <div
            class="group relative inline-block"
            :class="[
              message.role === 'user' ? 'ml-12' : 'mr-12'
            ]"
          >
            <div
              class="relative"
              :class="[
                'px-3 py-2 rounded-2xl shadow-sm break-words',
                'max-w-[calc(100vw-6rem)] sm:max-w-[460px]',
                message.role === 'user' 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                  : 'bg-white border border-gray-100'
              ]"
            >
              <div 
                class="text-[15px] leading-relaxed"
                v-html="message.role === 'assistant' ? detectAndFormatCode(message.content) : message.content"
              ></div>
              
              <!-- 复制按钮 - 优化移动端体验 -->
              <button
                v-if="message.role === 'assistant'"
                @click="(e) => copyToClipboard(message.content, e)"
                class="absolute -right-2 -top-2 p-1.5 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 sm:flex hidden items-center justify-center hover:bg-gray-50"
                title="复制内容"
              >
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                </svg>
              </button>
            </div>
            
            <!-- 消息时间和移动端复制按钮 -->
            <div 
              class="text-xs text-gray-400 mt-1 flex items-center justify-start gap-2"
              :class="[
                message.role === 'user' ? 'justify-end' : 'justify-start'
              ]"
            >
              <span>{{ new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) }}</span>
              <!-- 移动端复制按钮 - 始终显示 -->
              <button
                v-if="message.role === 'assistant'"
                @click="(e) => copyToClipboard(message.content, e)"
                class="sm:hidden p-1.5 hover:bg-gray-100 rounded-full active:bg-gray-200"
                title="复制内容"
              >
                <svg class="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- 用户头像 -->
          <div v-if="message.role === 'user'" class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center ml-2 flex-shrink-0 shadow-sm">
            <img src="@/assets/1.jpg" alt="" class="rounded-full w-full">
          </div>
        </div>

        <!-- 加载动画 -->
        <div v-if="uiState.isLoading" class="flex justify-start animate-fade-in mt-0">
          <div class="flex items-center gap-2 px-4 py-3 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div class="flex items-center gap-1">
              <div class="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
              <div class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
              <div class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>
            </div>
            <span class="text-sm text-gray-500">AI 正在思考...</span>
          </div>
        </div>

        <!-- 错误提示 -->
        <div v-if="uiState.error" class="text-red-500 text-center p-3 bg-red-50 rounded-lg border border-red-200 max-w-[460px] mx-auto animate-fade-in">
          {{ uiState.error }}
        </div>
      </div>
    </div>

    <!-- 复制成功提示 -->
    <div
      v-if="showCopyTip"
      class="fixed top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm animate-fade-in"
    >
      已复制到剪贴板
    </div>

    <!-- 输入框区域 -->
    <div class="bg-white/80 backdrop-blur-sm border-t sticky bottom-0 z-10">
      <div class="max-w-3xl mx-auto px-2 sm:px-4 py-2">
        <form @submit.prevent="handleSubmit" class="flex gap-2">
          <div class="flex-1 relative">
            <textarea
              ref="inputRef"
              v-model="inputMessage"
              rows="1"
              placeholder="输入您的问题..."
              @keydown="handleKeydown"
              @input="adjustTextareaHeight"
              class="w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none max-h-32 overflow-y-auto bg-white/90 shadow-sm text-[15px] sm:text-base"
              :class="[
                uiState.isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-text'
              ]"
              :disabled="uiState.isLoading"
            />
          </div>
          
          <!-- 发送按钮 -->
          <button
            type="submit"
            :disabled="uiState.isLoading || !inputMessage.trim()"
            class="flex-shrink-0 h-[40px] w-[40px] bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:bg-gray-200 disabled:from-gray-200 disabled:to-gray-200 transition-all duration-200 transform active:scale-95 shadow-sm flex items-center justify-center"
          >
            <svg 
              class="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </form>

        <!-- 输入提示 -->
        <div class="mt-1 text-xs text-gray-400 text-center">
          <span class="hidden sm:inline">按 Enter 发送，Shift + Enter 换行</span>
          <span class="sm:hidden">轻触发送或按回车发送</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}

/* 打字机光标动画 */
@keyframes typing-cursor {
  0%, 100% { border-right-color: transparent; }
  50% { border-right-color: currentColor; }
}

.typing-cursor {
  display: none;
}

textarea {
  -webkit-appearance: none;
  appearance: none;
  border-radius: 0.75rem;
  line-height: 1.5;
  min-height: 40px;
}

/* 自适应断点和安全区域 */
@media (max-width: 640px) {
  .max-w-3xl {
    max-width: 100%;
  }
  textarea {
    font-size: 15px !important;
    line-height: 1.4 !important;
  }
  
  /* 修复键盘弹出时的布局问题 */
  .h-[100dvh] {
    height: 100dvh;
  }
  
  /* 优化移动端输入框样式 */
  textarea {
    padding-right: 36px;
  }
  
  /* 移动端底部安全区域 */
  .bottom-0 {
    bottom: env(safe-area-inset-bottom, 0px);
  }
  
  /* 添加顶部安全区域 */
  .pt-safe {
    padding-top: env(safe-area-inset-top, 1rem);
  }
  
  /* 优化代码块在移动端的显示 */
  .code-block {
    margin: 0.5rem -0.75rem;
    border-radius: 0;
  }
  
  .hljs {
    padding: 0.75rem;
    font-size: 0.8125rem;
  }
  
  /* 优化复制按钮触摸区域 */
  button {
    min-height: 44px;
    min-width: 44px;
  }
}

/* 优化滚动条样式 */
.messagesContainer::-webkit-scrollbar {
  width: 4px;
}

.messagesContainer::-webkit-scrollbar-track {
  background: transparent;
}

.messagesContainer::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.messagesContainer::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.2);
}

/* 移除背景图相关样式 */
.messagesContainer {
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.2) 0%, rgba(249, 250, 251, 0.2) 100%);
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* 美化消息气泡 */
.rounded-2xl {
  border-radius: 1rem;
}

/* 优化阴影效果 */
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

/* 优化输入框样式 */
textarea:focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* 优化禁用状态 */
textarea:disabled {
  background-color: rgba(243, 244, 246, 0.9);
  cursor: not-allowed;
}

/* 代码块样式 */
.code-block {
  margin: 0.5rem 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #1a1a1a;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #2d2d2d;
  color: #e0e0e0;
  font-size: 0.875rem;
}

.code-lang {
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #888;
}

.hljs {
  padding: 1rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
</style> 